import axios from "axios";
import {PropsWithChildren, useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router";
import {http} from "@/api/HttpClient.ts";
import {getUserInfo} from "@/api/UserApi.ts";
import User from "@/auth/User.ts";
import {consumeRestorePage, saveCurrentPage} from "@/auth/AuthNavigation.ts";
import {useSessionKeepAlive} from "@/auth/useSessionKeepAlive.ts";
import {AuthContext} from "@/auth/AuthHooks.ts";
import {Button} from "@/components/ui/button.tsx";

type AuthState =
    | { status: "checking" }
    | { status: "authenticated"; user: User }
    | { status: "error" };

export function AuthProvider({children}: PropsWithChildren) {
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    const loginRedirected = useRef(false);
    const [state, setState] = useState<AuthState>({status: "checking"});
    const [verificationAttempt, setVerificationAttempt] = useState(0);

    useSessionKeepAlive(state.status === "authenticated");

    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);

    const showLogin = useCallback(() => {
        if (loginRedirected.current) return;
        loginRedirected.current = true;
        saveCurrentPage();
        navigateRef.current("/login", {replace: true});
    }, []);

    useEffect(() => {
        const interceptorId = http.interceptors.response.use(
            response => response,
            error => {
                if (axios.isAxiosError(error) && error.response?.status === 401) {
                    showLogin();
                }
                return Promise.reject(error);
            }
        );
        return () => http.interceptors.response.eject(interceptorId);
    }, [showLogin]);

    useEffect(() => {
        const controller = new AbortController();
        getUserInfo(controller.signal)
            .then(user => {
                if (controller.signal.aborted) return;
                setState({status: "authenticated", user});
                const restorePage = consumeRestorePage();
                if (restorePage) navigateRef.current(restorePage, {replace: true});
            })
            .catch(error => {
                if (controller.signal.aborted || axios.isCancel(error)) return;
                if (axios.isAxiosError(error) && error.response?.status === 401) {
                    // The response interceptor already navigated to login.
                    return;
                }
                setState({status: "error"});
        });
        return () => controller.abort();
    }, [showLogin, verificationAttempt]);

    if (state.status === "authenticated") {
        return <AuthContext.Provider value={state.user}>{children}</AuthContext.Provider>;
    }

    if (state.status === "error") {
        return <main className="grid min-h-full place-content-center gap-4 p-6 text-center">
            <title>Verbindungsfehler | DireDawn</title>
            <h1 className="text-xl font-medium">Login konnte nicht überprüft werden</h1>
            <p className="text-muted-foreground">Der Server ist momentan nicht erreichbar. Deine Anmeldung wurde nicht
                verworfen.</p>
            <Button onClick={() => {
                setState({status: "checking"});
                setVerificationAttempt(value => value + 1);
            }}>Erneut versuchen</Button>
        </main>;
    }

    return <main className="grid min-h-full place-content-center gap-3 p-6 text-center" aria-live="polite">
        <title>Anmeldung | DireDawn</title>
        <div className="mx-auto size-8 animate-spin rounded-full border-4 border-muted border-t-foreground"
             aria-hidden="true"/>
        <p>Anmeldung wird überprüft…</p>
    </main>;
}
